import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import moment from 'moment';
import { useVendorBankAccount } from '../store/slice/vendorBankAccountSlice';

function formatValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  return value;
}

function isVendorApproved(value) {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return null;
}

function approvalStatusClass(approved) {
  if (approved === true) return 'status-pill-success';
  if (approved === false) return 'status-pill-warning';
  return 'status-pill-muted';
}

function approvalStatusLabel(approved) {
  if (approved === true) return 'Approved';
  if (approved === false) return 'Pending';
  return 'Unknown';
}

function DetailRow({ label, value, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4 ${className}`}>
      <dt className="text-sm font-medium text-slate-500 shrink-0">{label}</dt>
      <dd className="text-sm text-slate-800 sm:text-right break-all">{value}</dd>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div className="card-premium p-6">
      <h2 className="text-lg font-semibold text-slate-800 mb-4">{title}</h2>
      <dl className="space-y-4">{children}</dl>
    </div>
  );
}

function VendorBankAccountDetailPage() {
  const { userId, id } = useParams();
  const lookupId = userId || id;
  const {
    selectedVendorBankAccount,
    detailLoading,
    error,
    fetchVendorBankAccountByUserId,
    fetchVendorBankAccountById,
    clearSelectedVendorBankAccount,
  } = useVendorBankAccount();

  useEffect(() => {
    if (!lookupId) return undefined;

    if (userId) {
      fetchVendorBankAccountByUserId(userId);
    } else {
      fetchVendorBankAccountById(id);
    }

    return () => clearSelectedVendorBankAccount();
  }, [
    lookupId,
    userId,
    id,
    fetchVendorBankAccountByUserId,
    fetchVendorBankAccountById,
    clearSelectedVendorBankAccount,
  ]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  if (detailLoading && !selectedVendorBankAccount) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-slate-500">Loading vendor bank account...</p>
      </div>
    );
  }

  if (!selectedVendorBankAccount) {
    return (
      <div className="space-y-4">
        <Link
          to="/vendor-bank-accounts"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Vendor Bank Accounts
        </Link>
        <div className="card-premium py-16 text-center text-slate-500">Vendor bank account not found.</div>
      </div>
    );
  }

  const account = selectedVendorBankAccount;
  const approved = isVendorApproved(account.vendor_is_approved);

  return (
    <div className="space-y-6">
      <Link
        to="/vendor-bank-accounts"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm font-medium"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Vendor Bank Accounts
      </Link>

      <div className="card-premium overflow-hidden">
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-amber-700 px-6 py-8 sm:px-8">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,255,255,0.18),_transparent_35%)]" />
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-medium text-amber-100/80">Bank Account #{account.id}</p>
              <h1 className="mt-1 font-display text-3xl font-bold text-white tracking-tight">
                {account.account_holder_name || '—'}
              </h1>
              <p className="mt-2 text-sm text-slate-200">
                {account.bank_name || 'Unknown bank'}
                {account.ifsc_code ? ` · ${account.ifsc_code}` : ''}
              </p>
            </div>
            <span className={`status-pill self-start sm:self-auto ${approvalStatusClass(approved)}`}>
              {approvalStatusLabel(approved)}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-100">
          <div className="bg-white p-5">
            <p className="text-sm font-medium text-slate-500">Account Number</p>
            <p className="mt-1 text-xl font-bold text-slate-800 font-mono break-all">
              {formatValue(account.account_number)}
            </p>
          </div>
          <div className="bg-white p-5">
            <p className="text-sm font-medium text-slate-500">Account Type</p>
            <p className="mt-1 text-2xl font-bold text-slate-800 capitalize">
              {formatValue(account.account_type)}
            </p>
          </div>
          <div className="bg-white p-5">
            <p className="text-sm font-medium text-slate-500">Vendor</p>
            <p className="mt-1 text-2xl font-bold text-slate-800">{formatValue(account.vendor_name)}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DetailSection title="Bank Account Details">
          <DetailRow label="Account ID" value={formatValue(account.id)} />
          <DetailRow label="Account Holder" value={formatValue(account.account_holder_name)} />
          <DetailRow label="Bank Name" value={formatValue(account.bank_name)} />
          <DetailRow label="Branch Name" value={formatValue(account.branch_name)} />
          <DetailRow label="Account Number" value={formatValue(account.account_number)} />
          <DetailRow label="IFSC Code" value={formatValue(account.ifsc_code)} />
          <DetailRow
            label="Account Type"
            value={<span className="capitalize">{formatValue(account.account_type)}</span>}
          />
          <DetailRow
            label="Created At"
            value={account.created_at ? moment(account.created_at).format('DD/MM/YYYY HH:mm') : '—'}
          />
          <DetailRow
            label="Updated At"
            value={account.updated_at ? moment(account.updated_at).format('DD/MM/YYYY HH:mm') : '—'}
          />
        </DetailSection>

        <DetailSection title="Vendor Details">
          <DetailRow label="Vendor Name" value={formatValue(account.vendor_name)} />
          <DetailRow label="Email" value={formatValue(account.vendor_email)} />
          <DetailRow label="Phone" value={formatValue(account.vendor_phone)} />
          <DetailRow label="User ID" value={formatValue(account.user_id)} />
          <DetailRow
            label="Vendor Approval"
            value={
              <span className={`status-pill ${approvalStatusClass(approved)}`}>
                {approvalStatusLabel(approved)}
              </span>
            }
          />
        </DetailSection>
      </div>
    </div>
  );
}

export default VendorBankAccountDetailPage;
